import {
  faArrowDownToLine,
  faArrowLeft,
  faArrowRight,
  faArrowUpRight,
  faCircleCheck,
} from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/cloudflare";
import { motion } from "framer-motion";

import { Quote } from "~/components/blog/quote";
import { EcosystemLogo } from "~/components/logos/ecosystem";
import { CallToAction } from "~/components/site/call-to-action";
import { ExternalLink } from "~/components/site/external-link";
import { item, Layout } from "~/components/site/layout";
import { ROUTE_DATA } from "~/lib/constants";
import { getMeta } from "~/lib/get-meta";
import { getUrls } from "~/lib/get-urls";
import type { Step } from "~/routes/case-studies";

const { desc, path, title, ogImage } = ROUTE_DATA.ethereumClassic;

const quote: Quote = {
  quote:
    "Brale is streamlining the regulated stablecoin issuance space and we are excited to work with them in developing Classic USD (USC), Ethereum Classic's premiere regulated stablecoin. Brale facilitates the issuance, redemption, and treasury management of USC ensuring USC is redeemable 1:1 for U.S. dollars. This allows our team to focus on building composable DeFi protocols for the Ethereum Classic ecosystem.",
  author: "Christopher Mercer",
  photo: "/assets/photos/ethereum-classic-logo.svg",
  title: "Founder",
  company: "EthereumClassic.com",
};

const steps: Step[] = [
  {
    image: "/assets/case-studies/ethereum-classic/ethereum-classic-step-1.svg",
    text: "The Ethereum Classic community mints USC with USD or by swapping stablecoins from other networks",
  },
  {
    image: "/assets/case-studies/ethereum-classic/ethereum-classic-step-2.svg",
    text: "USC is used for trading on ETCswap, for lending, and interacting with the DeFi ecosystem",
  },
  {
    image: "/assets/case-studies/ethereum-classic/ethereum-classic-step-3.svg",
    text: "USC can be redeemed anytime for U.S. dollars through Brale",
  },
];

const features: string[] = [
  "Stablecoin issuance",
  "Exchange listing",
  "Partner onboarding",
  "Onramp / offramp",
  "Token management",
  "Reserve management",
  "Program pages",
  "Multi-chain gateway",
  "Teams",
  "MPC signing",
  "Program monitor",
];

export function loader({ request }: LoaderFunctionArgs) {
  return {
    urls: getUrls({ path, request }),
  };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return data
    ? [
        ...getMeta({ desc, title, urls: data.urls }),
        { property: "og:image", content: `${data.urls.base}${ogImage}` },
        { name: "twitter:image", content: `${data.urls.base}${ogImage}` },
      ]
    : [];
};

export default function EthereumClassic(): JSX.Element {
  return (
    <Layout>
      <div className="section-first blog-post print:py-6">
        <div className="container">
          <motion.div variants={item} className="pb-12 print:pb-6">
            <a
              href="/case-studies"
              className="inline-flex items-center gap-2 mb-6 no-underline"
            >
              <FontAwesomeIcon
                className="h-4 w-4 text-secondary print:hidden"
                icon={faArrowLeft}
              />
              <div className="eyebrow !m-0 text-secondary">
                <span className="hidden print:inline">Brale - </span>
                <span>Case studies</span>
              </div>
            </a>
            <h1 className="h2 mt-0 mb-6 print:!text-2xl print:mb-0">
              How Brale unlocked DeFi for Ethereum Classic with a native
              stablecoin
            </h1>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-3 items-start lg:gap-16">
            <div className="lg:sticky top-8 mb-16 flex flex-col gap-4 print:mb-6">
              <motion.div
                variants={item}
                className="bg-secondary round p-4 lg:p-6 border border-primary"
              >
                <div className="flex flex-col items-start gap-y-6">
                  <div className="flex flex-col items-start gap-y-6 print:flex-row-reverse print:gap-4 print:items-center">
                    <span className="rounded-full border border-primary px-3 py-1 text-sm font-medium text-black dark:text-white">
                      Emerging Ecosystem
                    </span>
                    <EcosystemLogo ecosystemId={"ethereum-classic"} />
                  </div>
                  <div className="w-full flex flex-col sm:flex-row sm:justify-between lg:flex-col xl:flex-row gap-4 print:flex-row print:justify-between">
                    <div className="flex-grow print:flex print:items-end print:gap-2">
                      <div className="text-2xl font-semibold text-primary">
                        14 days
                      </div>
                      <div className="!mb-0">Time to launch</div>
                    </div>
                    <div className="flex-grow print:flex print:items-end print:gap-2">
                      <div className="text-2xl font-semibold text-primary">
                        2
                      </div>
                      <div className="!mb-0">Chains</div>
                    </div>
                    <div className="flex-grow print:flex print:items-end print:gap-2">
                      <div className="text-2xl font-semibold text-primary">
                        62%
                      </div>
                      <div className="!mb-0">Market dominance</div>
                    </div>
                  </div>
                </div>
              </motion.div>
              <motion.div
                variants={item}
                className="flex flex-col md:flex-row lg:flex-col gap-y-6 items-start md:items-center md:justify-between lg:items-start bg-secondary round p-4 lg:p-6 border border-primary print:flex-row print:justify-between print:items-center"
              >
                <div className="flex items-center gap-3">
                  <img
                    src="/assets/stablecoins/usc-logo.png"
                    className="h-8 w-8 rounded-full border border-primary"
                    alt="USC logo"
                  />
                  <span className="text-2xl font-semibold text-primary">
                    USC
                  </span>
                  <span className="text-2xl text-secondary font-normal whitespace-nowrap md:hidden xl:inline">
                    Classic USD
                  </span>
                </div>
                <div className="flex flex-wrap gap-3 print:hidden">
                  <a
                    className="btn secondary text-sm no-underline global-hover-transition !bg-white dark:!bg-gray-900 flex items-center gap-x-2"
                    title="Learn more about USC"
                    href="/stablecoins/USC"
                  >
                    <span>Learn more</span>
                    <FontAwesomeIcon
                      className="h-4 w-4 m-[1px]"
                      icon={faArrowRight}
                    />
                  </a>
                  <ExternalLink
                    className="btn secondary text-sm no-underline global-hover-transition !bg-white dark:!bg-gray-900 flex items-center gap-x-2"
                    title="Apply to buy USC"
                    href="https://app.brale.xyz/buy/signup/USC?ref=case-study"
                  >
                    <span>Apply to buy</span>
                    <FontAwesomeIcon
                      className="h-4 w-4 m-[1px]"
                      icon={faArrowUpRight}
                    />
                  </ExternalLink>
                  <ExternalLink
                    className="btn secondary text-sm no-underline global-hover-transition !bg-white dark:!bg-gray-900 flex items-center gap-x-2"
                    title="Download Case Study PDF"
                    href="/assets/case-studies/ethereum-classic/Ethereum-Classic-Brale-Case-Study.pdf"
                  >
                    <span>Download PDF</span>
                    <FontAwesomeIcon
                      className="h-4 w-4 m-[1px]"
                      icon={faArrowDownToLine}
                    />
                  </ExternalLink>
                </div>
              </motion.div>
            </div>
            <motion.div
              variants={item}
              className="col-span-2 flex flex-col gap-y-6"
            >
              <div>
                <div className="round border border-primary not-prose mb-10 bg-black overflow-clip print:hidden">
                  <img
                    alt="Ethereum Classic | Brale Case Study"
                    src="/assets/case-studies/ethereum-classic/cover.svg"
                  />
                </div>
                <h2 className="mt-0 print:!text-xl">Problem</h2>
                <p className="subheading mt-2 mb-12 lg:mb-0 print:!text-base print:mb-0">
                  The Ethereum Classic community needed a native stablecoin to
                  support its growing DeFi ecosystem. The stablecoin needed
                  blockchain interoperability, competitive fees, and regulatory
                  compliance with the world’s largest capital markets for global
                  adoption.
                </p>
                <h3 className="h4 print:!text-lg">Why Ethereum Classic?</h3>
                <p className="print:!text-sm">
                  Since Ethereum migrated to Proof of Stake (POS) through the
                  Merge, Ethereum Classic has been the recipient of the largest
                  cryptocurrency miner migration in blockchain history. Ethereum
                  Classic has quickly become the largest and most secure
                  proof-of-work blockchain with smart contract functionality.
                </p>
                <h3 className="h4 print:!text-lg">The challenges</h3>
                <p className="print:!text-sm print:break-after-page">
                  Ethereum Classic&apos;s efforts to build a decentralized
                  finance stack originally focused on integrating Multichain.org
                  bridge to bridge USDC, USDT, and DAI to Ethereum Classic, with
                  wrapped stablecoins traded on a Uniswap v2 AMM. Ultimately the
                  inherent security risks of wrapped stablecoins through
                  third-party bridges and the capital inefficiencies of the
                  Uniswap V2 protocol illustrated that Ethereum Classic needed a
                  reliable stablecoin deployed natively to its network.
                </p>
                <h3 className="h4 print:!text-lg">The situation</h3>
                <p className="print:!text-sm">
                  The EthereumClassic.com team needed a reliable stablecoin to
                  function as the foundational quote asset in the on-chain
                  ETC/USD market:
                </p>
                <ul>
                  <li className="print:!text-sm">
                    The stablecoin needed blockchain interoperability with DeFi
                    ecosystems on other networks like Ethereum, Solana, and
                    Stellar.
                  </li>
                  <li className="print:!text-sm">
                    The stablecoin needed capital efficiency in
                    collateralization and movement fees to competitively exist
                    alongside the mature ETC/USD centralized exchange markets.
                  </li>
                  <li className="print:!text-sm">
                    The stablecoin needed regulatory compliance with the world’s
                    largest capital markets for global adoption by individuals,
                    businesses, and governments.
                  </li>
                </ul>
                <p className="print:!text-sm">
                  This required either building a regulated entity to issue a
                  new stablecoin or working with an existing partner who could
                  implement Ethereum Classic quickly. After speaking with many
                  issuers, it became clear that the Ethereum Classic development
                  community would need to organize its own stablecoin effort.
                </p>
                <p className="print:!text-sm">
                  Meeting together at EthDenver 2024, the EthereumClassic.com
                  team identified Brale’s regulated stablecoin issuance platform
                  as the most desired solution in the marketplace. Brale
                  recognized a unique opportunity to add material value by
                  deploying the flagship stablecoin asset on a prominent
                  open-source, decentralized blockchain.
                </p>
                <div>
                  <Quote quote={quote} />
                </div>
                <h2 className="print:!text-xl">How Brale helped</h2>
                <p className="subheading mt-2 mb-12 lg:mb-0 print:!text-base print:mb-0 print:break-after-page">
                  Meeting together at EthDenver 2024, the EthereumClassic.com
                  team identified Brale’s regulated stablecoin issuance platform
                  as the most desired solution in the marketplace. Brale
                  recognized a unique opportunity to add material value by
                  deploying the flagship stablecoin asset on a prominent
                  open-source, decentralized blockchain.
                </p>
                <div>
                  <h3 className="h4 print:!text-lg">
                    Regulated issuance stack
                  </h3>
                  <p className="print:!text-sm">
                    ETCswap, the leading decentralized exchange on Ethereum
                    Classic, required a regulated partner with an experienced
                    track record in financial services to issue and manage the
                    anchor stablecoin on the network.
                  </p>
                  <div className="bg-secondary round p-6 lg:p-8 border border-primary flex flex-col sm:grid sm:grid-cols-2 gap-x-12 gap-y-6 mt-8 mb-12 print:p-4 print:my-6">
                    <div>
                      <h4 className="h5 mt-0 print:!text-base">Solution</h4>
                      <p className="m-0 print:!text-sm">
                        Brale&apos;s regulated issuance stack enabled the
                        Ethereum Classic community to issue Classic USD, a
                        native stablecoin with integrated on and offramps,
                        reserves management, and reporting.
                      </p>
                    </div>
                    <div>
                      <h4 className="h5 mt-0 print:!text-base">Outcome</h4>
                      <p className="m-0 print:!text-sm">
                        Partnering with Brale, ETCswap successfully launched
                        Classic USD (USC), which quickly became integrated into
                        the largest protocols on Ethereum Classic.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="print:break-after-page">
                  <h3 className="h4 print:!text-lg">
                    Custom blockchain implementation
                  </h3>
                  <p className="print:!text-sm">
                    ETCswap, the leading decentralized exchange on Ethereum
                    Classic, was initially unable to identify an issuance
                    partner who could implement Ethereum Classic quickly. This
                    prevented the launch of their V3 protocol.
                  </p>
                  <div className="bg-secondary round p-6 lg:p-8 border border-primary flex flex-col sm:grid sm:grid-cols-2 gap-x-12 gap-y-6 mt-8 mb-12 print:p-4 print:my-6">
                    <div>
                      <h4 className="h5 mt-0 print:!text-base">Solution</h4>
                      <p className="m-0 print:!text-sm">
                        Brale integrated the Ethereum Classic blockchain into
                        its issuance stack in under two weeks.
                      </p>
                    </div>
                    <div>
                      <h4 className="h5 mt-0 print:!text-base">Outcome</h4>
                      <p className="m-0 print:!text-sm">
                        After more than a year searching for a native stablecoin
                        solution, ETCswap partnered with Brale to issue the
                        first Ethereum Classic stablecoin, and Classic USD
                        became the largest stablecoin on the network enabling
                        the development community to build products around the
                        ETC/USC on-chain market.
                      </p>
                    </div>
                  </div>
                </div>
                <h2 className="print:!text-xl">
                  Classic USD, Ethereum Classic’s native stablecoin
                </h2>
                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-x-6 xl:gap-x-8 gap-y-8 border border-primary round p-6 xl:p-10 bg-secondary print:grid-cols-3 print:p-4 print:mt-6">
                  {steps.map((s, i) => (
                    <div key={i} className="flex flex-col">
                      <div className="flex-shrink-0 flex items-center justify-center">
                        <img
                          alt={`Graphic showing ${s.text}`}
                          src={s.image}
                          className="not-prose md:aspect-[400/466] w-full h-full print:aspect-[400/466]"
                        />
                      </div>
                      <div className="mt-8 flex-grow border-t-2 border-primary print:mt-0">
                        <div className="mt-4 print:!text-sm">{s.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <h2 className="print:!text-xl">Features used</h2>
                <div className="bg-secondary round p-6 lg:p-8 border border-primary">
                  <ul className="gap-y-3 leading-6 grid sm:grid-cols-2 xl:grid-cols-3 m-0 p-0 print:grid-cols-3">
                    {features.map((feature, i) => (
                      <li
                        key={i}
                        className="flex gap-x-3 m-0 p-0 text-base print:!text-sm print:gap-x-2 print:items-center"
                      >
                        <FontAwesomeIcon
                          className="h-6 w-6 text-green-500 print:h-4 print:w-4"
                          icon={faCircleCheck}
                        />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <CallToAction
                  seoRef="customer-story"
                  heading="Issue a native stablecoin for your ecosystem"
                  subheading="Reach out to our team to collaborate"
                  button="Schedule meeting"
                  ctaUrl="/contact"
                  className="print:hidden"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
